import { createContext, useContext } from "react";

/**
 * A host's say in how tall `DocumentListTable`'s scroller may grow.
 *
 * By default the list fills the window down to the bottom (`useFillToBottomHeight`),
 * which only accounts for the list's OWN following siblings. A host that puts
 * more content after the list — outside the list, further up the tree — has
 * that content pushed below the fold. The Brief page is such a host: its
 * Appendix section follows the Documents section and must stay reachable
 * however many files the brief holds.
 *
 * The list's scroller must stay the list's own: capping an ancestor instead
 * would leave the virtualiser measuring a viewport that never scrolls, and the
 * rows past it would never render.
 */
export interface DocumentListHeightLimit {
  /** Pixels to leave free below the list, for the host's content that follows it. */
  reserveBelow?: number;
  /** A CSS length the list never grows past (e.g. `"50vh"`). */
  cap?: string;
  /** Never shrink the list below this many pixels, however little room is left. */
  floor?: number;
  /**
   * Whether the list ends with room to scroll its last row clear of the chat
   * pill (the default). A host whose own content follows the list turns it off:
   * the list never reaches the window's bottom corner there, and the space only
   * makes a short list scroll, with a blank band under its last row.
   */
  chatClearance?: boolean;
}

export const DocumentListHeightLimitContext = createContext<DocumentListHeightLimit | null>(null);

function reserveFollowingContent(filled: number | undefined, limit: DocumentListHeightLimit) {
  if (filled === undefined) return undefined;
  return Math.max(limit.floor ?? 0, filled - (limit.reserveBelow ?? 0));
}

/**
 * The scroller's `max-height`: the filled height less the host's reserve,
 * never under the floor, never over the cap. `undefined` while the fill is
 * still unmeasured and no cap applies.
 */
export function documentListMaxHeight(
  filled: number | undefined,
  limit: DocumentListHeightLimit | null
): number | string | undefined {
  if (!limit) return filled;
  const reserved = reserveFollowingContent(filled, limit);
  if (!limit.cap) return reserved;
  return reserved === undefined ? limit.cap : `min(${reserved}px, ${limit.cap})`;
}

export function useDocumentListHeightLimit(): DocumentListHeightLimit | null {
  return useContext(DocumentListHeightLimitContext);
}
